import { UserRouteActionV1Model } from '@core/models/entities/implementations/user-route-action-v1';
import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';
import {
  JobPlacement,
  PlanEditGesture,
  RouteResources
} from '@core/models/plan-edit-gesture.model';
import { parseISO8601Duration, toISO8601Duration } from '@core/utils/time-utils';

import {
  actionFinish,
  actionStart,
  awaitsTiming,
  departureAction,
  finishLeg,
  pinOrigin,
  startLeg,
  stepsBeforeTimeWindow
} from './route-timing';

export const PREDICTED_ACTION_PREFIX = 'predicted:';

/** A predicted action's uid is the client's own: DL does not know it until `applied`. */
export function isPredictedActionUid(uid: string): boolean {
  return uid.startsWith(PREDICTED_ACTION_PREFIX);
}

type Routes = Readonly<Record<string, UserRouteV1Model>>;

interface Pick {
  actionUid: string;
  /** Takes only this job's movements; absent takes the whole stop. */
  jobUid?: string;
}

function cloneOf(routes: Routes, routeUid: string): UserRouteV1Model | undefined {
  return routes[routeUid] ? structuredClone(routes[routeUid]) : undefined;
}

function shiftIso(iso: string, shiftMs: number): string {
  return new Date(new Date(iso).getTime() + shiftMs).toISOString();
}

/**
 * Shortens the waits ahead of the time window by up to `ms`, latest first, dropping a wait shrunk
 * to nothing, as DL does; returns the shift they could not absorb, which is what the action's
 * start has to slide by.
 */
function shrinkWaitsBeforeTimeWindow(action: UserRouteActionV1Model, ms: number): number {
  let remaining = ms;
  const steps = action.steps_from_previous_finish;
  for (let i = stepsBeforeTimeWindow(action).length - 1; i >= 0 && remaining > 0; i--) {
    if (steps[i].kind !== 'Wait') continue;
    const wait = parseISO8601Duration(steps[i].duration).totalMilliseconds;
    const absorbed = Math.min(wait, remaining);
    remaining -= absorbed;
    if (absorbed === wait) steps.splice(i, 1);
    else steps[i].duration = toISO8601Duration({ totalMilliseconds: wait - absorbed });
  }
  return -remaining;
}

/** Adds `ms` of waiting right before the time window, growing a wait already there, as DL does. */
function growWaitBeforeTimeWindow(action: UserRouteActionV1Model, ms: number): void {
  const steps = action.steps_from_previous_finish;
  const end = stepsBeforeTimeWindow(action).length;
  const last = steps[end - 1];
  if (last?.kind === 'Wait') {
    last.duration = toISO8601Duration({
      totalMilliseconds: parseISO8601Duration(last.duration).totalMilliseconds + ms
    });
  } else {
    steps.splice(end, 0, {
      kind: 'Wait',
      duration: toISO8601Duration({ totalMilliseconds: ms })
    });
  }
}

function shiftServiceTimes(action: UserRouteActionV1Model, shiftMs: number): void {
  for (const movement of action.movements) {
    movement.service_start = shiftIso(movement.service_start, shiftMs);
    movement.service_end = shiftIso(movement.service_end, shiftMs);
  }
  for (const usage of action.infrastructure_usages ?? []) {
    usage.start = shiftIso(usage.start, shiftMs);
    usage.end = shiftIso(usage.end, shiftMs);
  }
}

function shiftStart(action: UserRouteActionV1Model, shiftMs: number): void {
  action.scheduled_first_step_start_datetime = shiftIso(
    action.scheduled_first_step_start_datetime,
    shiftMs
  );
}

/** `EndOf` goes before the route's finish leg, which is never a stop, or last on a route with none. */
function insertionIndex(
  route: UserRouteV1Model,
  placement: Exclude<JobPlacement, { kind: 'MergedInto' }>
): number {
  if (placement.kind === 'Before') {
    return route.actions.findIndex((action) => action.uid === placement.actionUid);
  }
  return finishLeg(route) ? route.actions.length - 1 : route.actions.length;
}

/**
 * Inserts a moved action at `index`, starting at the finish of the action before it with only its
 * service: the steps that led to it belong to its old place, and DL times the route anew. Drawn at
 * its old time instead, a stop moved within its route would show where it was until DL answers.
 */
function landUntimed(
  route: UserRouteV1Model,
  index: number,
  action: UserRouteActionV1Model
): void {
  const before = route.actions[index - 1];
  const after = route.actions[index];
  // A route with no action yet, a new one, gives the action nowhere to land but where it was.
  const landing = before
    ? actionFinish(before)
    : after
      ? actionStart(after)
      : actionStart(action);
  action.scheduled_first_step_start_datetime = landing.toISOString();
  action.steps_from_previous_finish = action.steps_from_previous_finish.filter(
    (step) => step.kind === 'ActiveService'
  );
  route.actions.splice(index, 0, action);
}

function reorder(
  routes: Routes,
  routeUid: string,
  actionUids: string[],
  placement: Exclude<JobPlacement, { kind: 'MergedInto' }>
): Record<string, UserRouteV1Model> {
  const route = cloneOf(routes, routeUid);
  if (!route) {
    return {};
  }
  const moved = route.actions.filter((action) => actionUids.includes(action.uid));
  route.actions = route.actions.filter((action) => !actionUids.includes(action.uid));
  const index = insertionIndex(route, placement);
  moved.forEach((action, offset) => landUntimed(route, index + offset, action));
  return { [routeUid]: route };
}

function relocate(
  routes: Routes,
  fromRouteUid: string,
  picks: Pick[],
  placement: JobPlacement
): Record<string, UserRouteV1Model> {
  const source = cloneOf(routes, fromRouteUid);
  if (!source) {
    return {};
  }
  const taken: UserRouteActionV1Model[] = [];
  for (const pick of picks) {
    const action = source.actions.find((candidate) => candidate.uid === pick.actionUid);
    if (!action) {
      continue;
    }
    const takenMovements = pick.jobUid
      ? action.movements.filter((movement) => movement.job_uid === pick.jobUid)
      : action.movements;
    taken.push({ ...structuredClone(action), movements: structuredClone(takenMovements) });
    action.movements = pick.jobUid
      ? action.movements.filter((movement) => movement.job_uid !== pick.jobUid)
      : [];
    if (action.movements.length === 0) {
      source.actions = source.actions.filter((candidate) => candidate.uid !== action.uid);
    }
  }

  const target =
    placement.routeUid === fromRouteUid ? source : cloneOf(routes, placement.routeUid);
  const result: Record<string, UserRouteV1Model> = { [fromRouteUid]: source };
  if (!target) {
    return result;
  }
  result[placement.routeUid] = target;

  if (placement.kind === 'MergedInto') {
    const host = target.actions.find((action) => action.uid === placement.actionUid);
    host?.movements.push(...taken.flatMap((action) => action.movements));
    return result;
  }

  const index = insertionIndex(target, placement);
  taken.forEach((copy, offset) => {
    copy.uid = `${PREDICTED_ACTION_PREFIX}${copy.uid}`;
    copy.route_uid = target.uid;
    landUntimed(target, index + offset, copy);
  });
  return result;
}

function applyCompany(route: UserRouteV1Model, companyUid: string | null): void {
  route.company_uid = companyUid ?? undefined;
}

function applyResources(route: UserRouteV1Model, resources: RouteResources): void {
  route.vehicle_uid = resources.vehicleUid;
  route.driver_uid = resources.driverUid ?? undefined;
  route.trailer_uid = resources.trailerUid ?? undefined;
}

function withResources(
  routes: Routes,
  ...all: RouteResources[]
): Record<string, UserRouteV1Model> {
  const result: Record<string, UserRouteV1Model> = {};
  for (const resources of all) {
    const route = cloneOf(routes, resources.routeUid);
    if (route) {
      applyResources(route, resources);
      result[resources.routeUid] = route;
    }
  }
  return result;
}

/**
 * What the touched routes look like once the gesture applies, before the server answers. It moves
 * only what the gesture names: a time downstream of an edit is the server's to compute. A touched
 * route missing from `routes` is not drawn, so it is skipped.
 */
export function predict(
  gesture: PlanEditGesture,
  routes: Routes
): Record<string, UserRouteV1Model> {
  const clears = 'clearsCompanyOf' in gesture ? (gesture.clearsCompanyOf ?? []) : [];
  const cleared: Record<string, UserRouteV1Model> = {};
  for (const routeUid of clears) {
    const route = cloneOf(routes, routeUid);
    if (!route) continue;
    applyCompany(route, null);
    cleared[routeUid] = route;
  }
  return { ...cleared, ...predictOwn(gesture, { ...routes, ...cleared }) };
}

function predictOwn(
  gesture: PlanEditGesture,
  routes: Routes
): Record<string, UserRouteV1Model> {
  switch (gesture.kind) {
    case 'pin': {
      const route = cloneOf(routes, gesture.routeUid);
      const action = route?.actions.find(
        (candidate) => candidate.uid === gesture.actionUid
      );
      if (!route || !action) {
        return {};
      }
      const shift = new Date(gesture.at).getTime() - pinOrigin(action).getTime();
      shiftServiceTimes(action, shift);
      shiftStart(action, shift >= 0 ? shift : shrinkWaitsBeforeTimeWindow(action, -shift));
      return { [gesture.routeUid]: route };
    }
    case 'release-pin':
      return {};
    case 'set-route-start': {
      const at = gesture.at;
      const route = at === null ? undefined : cloneOf(routes, gesture.routeUid);
      const first = route && departureAction(route);
      if (at === null || !route || !first) {
        return {};
      }
      const shift = new Date(at).getTime() - actionStart(first).getTime();
      // As DL departs a route: a stop opening it keeps when it starts inside its time window and
      // waits ahead of it for an earlier start; anything else slides whole.
      if (shift < 0 && startLeg(route) === null && !awaitsTiming(first)) {
        growWaitBeforeTimeWindow(first, -shift);
      } else {
        shiftServiceTimes(first, shift);
      }
      shiftStart(first, shift);
      return { [gesture.routeUid]: route };
    }
    case 'set-lock': {
      const route = cloneOf(routes, gesture.routeUid);
      if (!route) {
        return {};
      }
      route.lock_cfg = gesture.lockCfg;
      return { [gesture.routeUid]: route };
    }
    case 'reorder':
      return reorder(routes, gesture.routeUid, [gesture.actionUid], gesture.placement);
    case 'move-stop':
      if (
        gesture.placement.routeUid === gesture.fromRouteUid &&
        gesture.placement.kind !== 'MergedInto'
      ) {
        return reorder(
          routes,
          gesture.fromRouteUid,
          [gesture.actionUid],
          gesture.placement
        );
      }
      return relocate(
        routes,
        gesture.fromRouteUid,
        [{ actionUid: gesture.actionUid }],
        gesture.placement
      );
    case 'move-job':
      return relocate(
        routes,
        gesture.fromRouteUid,
        [{ actionUid: gesture.fromActionUid, jobUid: gesture.jobUid }],
        gesture.placement
      );
    case 'move-tour':
      if (gesture.placement.routeUid === gesture.fromRouteUid) {
        return reorder(routes, gesture.fromRouteUid, gesture.actionUids, gesture.placement);
      }
      return relocate(
        routes,
        gesture.fromRouteUid,
        gesture.actionUids.map((actionUid) => ({ actionUid })),
        gesture.placement
      );
    case 'set-resources': {
      const result = withResources(routes, gesture.resources);
      if (gesture.companyUid !== undefined && result[gesture.resources.routeUid]) {
        applyCompany(result[gesture.resources.routeUid], gesture.companyUid);
      }
      return result;
    }
    case 'set-company': {
      const route = cloneOf(routes, gesture.routeUid);
      if (!route) return {};
      applyCompany(route, gesture.companyUid);
      return { [gesture.routeUid]: route };
    }
    case 'swap-resources':
      return withResources(routes, gesture.a, gesture.b);
    case 'open-route': {
      const opened: UserRouteV1Model = {
        uid: gesture.routeRef,
        vehicle_uid: gesture.vehicleUid,
        driver_uid: gesture.driverUid ?? undefined,
        trailer_uid: gesture.trailerUid ?? undefined,
        company_uid: gesture.companyUid ?? undefined,
        lock_cfg: 'Unlocked',
        actions: [],
        kpis: [],
        custom_data: {},
        vehicle_registration: null
      };
      const withOpened = { ...routes, [gesture.routeRef]: opened };
      return {
        [gesture.routeRef]: opened,
        ...(gesture.then ? predict(gesture.then, withOpened) : {})
      };
    }
    case 'remove-route': {
      const route = cloneOf(routes, gesture.routeUid);
      if (!route) return {};
      route.actions = [];
      return { [gesture.routeUid]: route };
    }
  }
}
